import { Link, usePage } from '@inertiajs/react';
import { m, useScroll, useTransform } from 'framer-motion';
import {
    ArrowRight,
    MapPin,
    CalendarCheck,
    UserCheck,
    ShieldCheck,
} from 'lucide-react';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { fadeUp, stagger } from '@/lib/landing-motion';
import { dashboard, login, bantuan } from '@/routes';

export default function LandingHero({
    name,
    logo,
}: {
    name: string;
    logo?: string;
}) {
    const { auth } = usePage().props;
    const sectionRef = useRef<HTMLElement>(null);

    // Scroll-scrubbed parallax: the preview frame rises and tilts flat as you scroll.
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end start'],
    });
    const previewY = useTransform(scrollYProgress, [0, 1], [0, -90]);
    const previewScale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);
    const previewOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.4]);
    const glowOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0]);

    return (
        <section
            ref={sectionRef}
            className="relative overflow-hidden px-5 pt-28 pb-20 lg:px-8 lg:pt-36"
        >
            {/* Background: dot grid + single soft glow */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="bg-dot-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)] text-foreground/[0.04]" />
                <m.div
                    style={{ opacity: glowOpacity }}
                    className="absolute -top-32 left-1/2 size-[520px] -translate-x-1/2 rounded-full bg-primary/15 blur-[120px]"
                />
            </div>

            <m.div
                variants={stagger}
                initial="hidden"
                animate="show"
                className="mx-auto max-w-3xl text-center"
            >
                {/* Eyebrow */}
                <m.div
                    variants={fadeUp}
                    className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-3.5 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur-sm"
                >
                    {logo ? (
                        <img
                            src={logo}
                            alt={name}
                            className="size-4 rounded-full object-cover"
                        />
                    ) : (
                        <span className="size-1.5 rounded-full bg-primary" />
                    )}
                    Presensi digital untuk {name}
                </m.div>

                {/* Headline */}
                <m.h1
                    variants={fadeUp}
                    className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl lg:text-6xl"
                >
                    Kehadiran santri,{' '}
                    <span className="text-primary">tercatat tepat</span> di
                    tempat dan waktunya.
                </m.h1>

                {/* Sub-heading */}
                <m.p
                    variants={fadeUp}
                    className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-pretty text-muted-foreground sm:text-lg"
                >
                    Presensi berbasis geofencing yang akurat dan terenkripsi —
                    menumbuhkan kedisiplinan dan menjaga keamanan data, cukup
                    dari genggaman.
                </m.p>

                {/* CTA buttons */}
                <m.div
                    variants={fadeUp}
                    className="mt-8 flex flex-wrap items-center justify-center gap-3"
                >
                    <Link href={auth.user ? dashboard.url() : login.url()}>
                        <Button className="group h-11 rounded-full px-6 text-sm font-medium">
                            {auth.user ? 'Buka Dashboard' : 'Mulai Sekarang'}
                            <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5" />
                        </Button>
                    </Link>
                    <Link href={bantuan.url()}>
                        <Button
                            variant="outline"
                            className="h-11 rounded-full border-border/70 px-6 text-sm font-medium"
                        >
                            Dokumentasi
                        </Button>
                    </Link>
                </m.div>
            </m.div>

            {/* Wide framed app preview with scroll parallax */}
            <m.div
                style={{
                    y: previewY,
                    scale: previewScale,
                    opacity: previewOpacity,
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.22, 1, 0.36, 1],
                }}
                className="mx-auto mt-16 max-w-5xl"
            >
                <div className="rounded-2xl border border-border/60 bg-card/70 p-2 shadow-2xl shadow-primary/5 backdrop-blur-sm">
                    {/* Window chrome */}
                    <div className="flex items-center gap-1.5 px-3 py-2">
                        <span className="size-2.5 rounded-full bg-destructive/40" />
                        <span className="size-2.5 rounded-full bg-[#ffd709]/60" />
                        <span className="size-2.5 rounded-full bg-primary/40" />
                        <div className="ml-3 h-5 flex-1 rounded-md bg-muted/60" />
                    </div>

                    <div className="grid gap-3 rounded-xl bg-background/60 p-3 sm:grid-cols-5">
                        {/* Map panel */}
                        <div className="relative h-56 overflow-hidden rounded-lg border border-border/50 bg-gradient-to-br from-primary/10 to-[#ffd709]/5 sm:col-span-3 sm:h-72">
                            <div className="bg-dot-grid absolute inset-0 text-primary/15" />
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                <m.div
                                    animate={{
                                        scale: [1, 1.4, 1],
                                        opacity: [0.35, 0.05, 0.35],
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                    className="absolute -inset-10 rounded-full border border-primary/50 bg-primary/10"
                                />
                                <div className="relative flex size-9 items-center justify-center rounded-full bg-primary shadow-lg shadow-primary/40">
                                    <MapPin className="size-4 fill-white text-white" />
                                </div>
                            </div>
                            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-lg border border-border/50 bg-background/90 px-2.5 py-1.5 text-xs font-medium text-foreground">
                                <span className="size-1.5 rounded-full bg-primary" />
                                Dalam zona geofence
                            </div>
                        </div>

                        {/* Status panel */}
                        <div className="flex flex-col gap-3 sm:col-span-2">
                            {[
                                {
                                    icon: CalendarCheck,
                                    label: 'Presensi Santri',
                                    desc: 'Tercatat otomatis & akurat',
                                },
                                {
                                    icon: UserCheck,
                                    label: 'Pengajuan Izin',
                                    desc: 'Ditinjau langsung admin',
                                },
                                {
                                    icon: ShieldCheck,
                                    label: 'Data Terenkripsi',
                                    desc: 'AES-256 end-to-end',
                                },
                            ].map(({ icon: Icon, label, desc }) => (
                                <div
                                    key={label}
                                    className="flex items-center gap-3 rounded-lg border border-border/50 bg-card/60 p-3"
                                >
                                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Icon className="size-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-foreground">
                                            {label}
                                        </div>
                                        <div className="truncate text-xs text-muted-foreground">
                                            {desc}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </m.div>
        </section>
    );
}
